import {useState, type FocusEvent, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Hint} from '@/components/ui/hint'
import {InfoTip} from '@/components/ui/info-tip'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {ConfirmarAltaDialog} from '@/components/confirmar-identidad-dialog'
import {pideConfirmarIdentidad, useInviteResponsable, type AccessGrant} from '@/hooks/platform/use-access'
import {translateApiError} from '@/lib/errors'
import {TipoDocumentoSelect} from '@/components/tipo-documento-select'
import {DOCUMENTO_MAX, type TipoDocumento} from '@/lib/documento'
import {validateCedula, validateEmail, validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

type FieldErrors = {cedula?: string; name?: string; lastName?: string; email?: string}

function validateAlta(
  cedula: string,
  tipoDocumento: TipoDocumento,
  name: string,
  lastName: string,
  email: string
): FieldErrors {
  const errors: FieldErrors = {}
  const cedulaErr = validateCedula(cedula, tipoDocumento)
  if (cedulaErr) errors.cedula = cedulaErr
  const nameErr = validateRequired(name)
  if (nameErr) errors.name = nameErr
  const lastNameErr = validateRequired(lastName)
  if (lastNameErr) errors.lastName = lastNameErr
  const emailErr = validateEmail(email)
  if (emailErr) errors.email = emailErr
  return errors
}

/** `onInvited` recibe la membresía creada para que quien abrió el diálogo la use en el acto. */
export function InvitarExternoDialog({
  businessId,
  open,
  onOpenChange,
  onInvited,
}: {
  businessId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onInvited?: (grant: AccessGrant) => void
}) {
  const {t} = useTranslation()
  const invite = useInviteResponsable(businessId)
  // Ref de callback: Radix monta el contenido después del primer efecto y `ref.current` seguiría en null.
  const [dialogo, setDialogo] = useState<HTMLElement | null>(null)
  const [cedula, setCedula] = useState('')
  const [tipoDocumento, setTipoDocumento] = useState<TipoDocumento>('CC')
  const [name, setName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [error, setError] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState(false)

  function close() {
    setCedula('')
    setName('')
    setLastName('')
    setEmail('')
    setFieldErrors({})
    setError(null)
    setConfirmando(false)
    invite.reset()
    onOpenChange(false)
  }

  function blurField<K extends keyof FieldErrors>(field: K, validate: (raw: string) => string | null) {
    return (e: FocusEvent<HTMLInputElement>) =>
      setFieldErrors((fe) => ({...fe, [field]: validate(e.target.value) ?? undefined}))
  }

  function invitar(confirmar_identidad: boolean) {
    invite.mutate(
      {
        cedula: cedula.trim(),
        tipo_documento: tipoDocumento,
        name: name.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        confirmar_identidad,
      },
      {
        onSuccess: (grant) => {
          onInvited?.(grant)
          close()
        },
        onError: (err) => {
          setConfirmando(false)
          if (pideConfirmarIdentidad(err)) {
            setConfirmando(true)
            return
          }
          setError(translateApiError(err))
        },
      }
    )
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    const errors = validateAlta(cedula, tipoDocumento, name, lastName, email)
    setFieldErrors(errors)
    if (Object.values(errors).some(Boolean)) return
    invitar(false)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="invitar-responsable-dialog" ref={setDialogo}>
        <DialogHeader>
          <div className="flex items-center gap-1.5">
            <DialogTitle>{t('externoAlta.title')}</DialogTitle>
            <InfoTip
              label={t('externoAlta.tipLabel')}
              tip={t('externoAlta.tip')}
              contenedor={dialogo}
              testId="externo-alta-tip"
            />
          </div>
          <DialogDescription>{t('externoAlta.body')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="invitar-cedula">{t('documento.label')}</Label>
            <div className="flex gap-2">
              <TipoDocumentoSelect
                value={tipoDocumento}
                onChange={(tipo) => {
                  setTipoDocumento(tipo)
                  setFieldErrors((fe) => ({...fe, cedula: undefined}))
                }}
                testId="invitar-tipo-documento"
              />
              <Input
                id="invitar-cedula"
                data-testid="invitar-cedula"
                className="flex-1 font-mono"
                maxLength={DOCUMENTO_MAX[tipoDocumento]}
                value={cedula}
                onChange={(e) => {
                  setCedula(e.target.value)
                  setFieldErrors((fe) => ({...fe, cedula: undefined}))
                }}
                onBlur={blurField('cedula', (raw) => validateCedula(raw, tipoDocumento))}
                inputMode={tipoDocumento === 'PA' ? 'text' : 'numeric'}
                aria-invalid={!!fieldErrors.cedula}
              />
            </div>
            <FieldError error={fieldErrors.cedula} />
            <Hint>{t('externoAlta.cedulaHint')}</Hint>
            {/* El endpoint engancha la identidad existente (dedup por cédula en `User`); consultarla
                antes sería sondear una cédula ajena, así que solo se avisa. */}
            <Hint testId="invitar-dedup-aviso">{t('externoAlta.dedup')}</Hint>
          </div>
          <div className="flex flex-col gap-4">
            <div className="space-y-2">
              <Label htmlFor="invitar-name">{t('externoAlta.name')}</Label>
              <Input
                maxLength={tope('NOM')}
                id="invitar-name"
                data-testid="invitar-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  setFieldErrors((fe) => ({...fe, name: undefined}))
                }}
                onBlur={blurField('name', validateRequired)}
                aria-invalid={!!fieldErrors.name}
              />
              <FieldError error={fieldErrors.name} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invitar-last-name">{t('externoAlta.lastName')}</Label>
              <Input
                maxLength={tope('NOM')}
                id="invitar-last-name"
                data-testid="invitar-last-name"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value)
                  setFieldErrors((fe) => ({...fe, lastName: undefined}))
                }}
                onBlur={blurField('lastName', validateRequired)}
                aria-invalid={!!fieldErrors.lastName}
              />
              <FieldError error={fieldErrors.lastName} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="invitar-email">{t('externoAlta.email')}</Label>
            <Input
              maxLength={tope('MAIL')}
              id="invitar-email"
              data-testid="invitar-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setFieldErrors((fe) => ({...fe, email: undefined}))
              }}
              onBlur={blurField('email', validateEmail)}
              aria-invalid={!!fieldErrors.email}
            />
            <FieldError error={fieldErrors.email} />
          </div>
          <FormDialogFooter
            error={error}
            pending={invite.isPending}
            submitLabel={t('externoAlta.submit')}
            onCancel={close}
          />
        </form>
      </DialogContent>
      <ConfirmarAltaDialog
        open={confirmando}
        onOpenChange={setConfirmando}
        nombre={`${name.trim()} ${lastName.trim()}`}
        cedula={cedula.trim()}
        onConfirm={() => invitar(true)}
        pending={invite.isPending}
      />
    </Dialog>
  )
}
